import React from 'react';
import { humanizeKey } from '../../utils/humanizeKey';
import { hasMultipleOptions, getComponentOptions, getOptionTotal } from '../Quotes/optionUtils';
import { 
  Heart,
  Home,
  MapPin,
  Calendar,
  Users,
  Sparkles,
  Wine,
  Camera,
  Plane,
  Car,
  Utensils,
  Music,
  CheckCircle2,
  Flower2,
  Mail,
  Phone
} from 'lucide-react';
import { TEMPLATE_PALETTES } from './templatePalettes';

const PALETTE = TEMPLATE_PALETTES.destination_wedding;

const DestinationWeddingTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates elegantly
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get couple names from title
  const getCoupleNames = () => {
    if (quote.quoteTitle) {
      return quote.quoteTitle;
    }
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.firstName} & Partner`;
    }
    return 'Your Destination Wedding';
  };

  // Categorize components
  const categorizeComponents = () => {
    const venue = [];
    const celebration = [];
    const logistics = [];

    quote.components?.forEach(component => {
      const type = component.type?.toLowerCase() || '';
      const title = component.title?.toLowerCase() || '';

      if (type.includes('hotel') || type.includes('resort') || title.includes('resort') || title.includes('hotel')) {
        venue.push(component);
      } else if (type.includes('flight') || type.includes('car') || type.includes('transfer') || title.includes('flight') || title.includes('transfer')) {
        logistics.push(component);
      } else {
        celebration.push(component);
      }
    });

    return { venue, celebration, logistics };
  };

  const { venue, celebration, logistics } = categorizeComponents();

  // Get celebration icon
  const getCelebrationIcon = (component) => {
    const type = component.type?.toLowerCase() || '';
    const title = component.title?.toLowerCase() || '';

    if (title.includes('ceremony') || title.includes('wedding')) {
      return <Heart className="w-6 h-6 text-[var(--primary)]" />;
    } else if (title.includes('reception') || title.includes('party') || title.includes('dinner')) {
      return <Wine className="w-6 h-6 text-[var(--primary)]" />;
    } else if (title.includes('photo') || title.includes('photographer')) {
      return <Camera className="w-6 h-6 text-[var(--primary)]" />;
    } else if (type.includes('dining') || type.includes('restaurant')) {
      return <Utensils className="w-6 h-6 text-[var(--primary)]" />;
    } else if (title.includes('music') || title.includes('entertainment')) {
      return <Music className="w-6 h-6 text-[var(--primary)]" />;
    }
    return <Sparkles className="w-6 h-6 text-[var(--primary)]" />;
  };

  return (
    <div className="min-h-screen" style={{ ...PALETTE, backgroundColor: 'var(--background)' }}>
      {/* Romantic Hero Section */}
      <div className="relative min-h-[40vh] flex items-center justify-center bg-[var(--muted)]">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/20 to-white/40"></div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <Heart className="w-16 h-16 text-[var(--primary)] mx-auto mb-8 animate-pulse" />
          
          <h1 className="font-serif text-5xl md:text-7xl text-[var(--foreground)] mb-6 tracking-wide leading-tight">
            {getCoupleNames()}
          </h1>
          
          <div className="flex items-center justify-center gap-4 text-[var(--muted-foreground)]">
            <div className="w-16 h-px bg-[var(--accent)]"></div>
            <span className="font-serif text-xl tracking-widest">Destination Wedding Proposal</span>
            <div className="w-16 h-px bg-[var(--accent)]"></div>
          </div>

          {quote.travelDates?.start && (
            <p className="mt-8 text-[var(--muted-foreground)] text-lg font-light tracking-wide">
              {formatDate(quote.travelDates.start)}
              {quote.travelDates.end && ` - ${formatDate(quote.travelDates.end)}`}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16">
        
        {/* Part 1: The Venue/Resort */}
        {venue.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="font-serif text-4xl text-[var(--foreground)] mb-3 tracking-wide">Your Wedding Venue</h2>
              <p className="text-[var(--muted-foreground)] font-light">Where your celebration begins</p>
              <div className="w-24 h-px bg-[var(--accent)] mx-auto mt-4"></div>
            </div>

            {venue.map((component, index) => (
              <div
                key={component.id || index}
                className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-[var(--border)]"
              >
                <div className="bg-[var(--muted)] p-10 border-b-2 border-[var(--border)]">
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <Home className="w-10 h-10 text-[var(--primary)]" />
                    <h3 className="font-serif text-4xl text-[var(--foreground)] tracking-wide">
                      {component.title || 'Wedding Resort'}
                    </h3>
                  </div>
                  
                  {component.data?.location && (
                    <div className="flex items-center justify-center gap-2 text-[var(--muted-foreground)]">
                      <MapPin className="w-5 h-5 text-[var(--primary)]" />
                      <span className="text-lg">{component.data.location}</span>
                    </div>
                  )}
                </div>

                <div className="p-10">
                  {/* Room Category & Details */}
                  {component.data && Object.keys(component.data).length > 0 && (
                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                      {Object.entries(component.data).map(([key, value]) => {
                        if (!value || key === 'isOption' || key === 'optionDescription') return null;
                        
                        const isPriceField = key.toLowerCase().includes('price') || 
                                             key.toLowerCase().includes('cost') || 
                                             key.toLowerCase().includes('fee') ||
                                             key.toLowerCase().includes('total') ||
                                             key.toLowerCase().includes('deposit');
                        const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                        
                        let displayValue = String(value);
                        if (isPriceField && isNumeric) {
                          displayValue = formatCurrency(parseFloat(value));
                        }
                        
                        return (
                          <div key={key} className="bg-[var(--muted)] rounded-lg p-6 border border-[var(--border)]">
                            <div className="text-xs uppercase tracking-widest text-[var(--primary)] mb-2 font-semibold">
                              {humanizeKey(key)}
                            </div>
                            <div className="text-[var(--foreground)] text-lg font-semibold">
                              {displayValue}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Venue Investment */}
                  <div className="pt-8 border-t-2 border-[var(--border)] flex justify-between items-center">
                    <span className="text-[var(--muted-foreground)] font-serif text-xl">Venue Investment</span>
                    <span className="font-serif text-3xl text-[var(--foreground)]">{formatCurrency(component.price)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Part 2: The Celebration Itinerary */}
        {celebration.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="font-serif text-4xl text-[var(--foreground)] mb-3 tracking-wide">Celebration Itinerary</h2>
              <p className="text-[var(--muted-foreground)] font-light">Your special moments, beautifully planned</p>
              <div className="w-24 h-px bg-[var(--accent)] mx-auto mt-4"></div>
            </div>

            <div className="space-y-6">
              {celebration.map((component, index) => (
                <div
                  key={component.id || index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-[var(--border)] hover:shadow-xl transition-shadow"
                >
                  <div className="p-8">
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex items-start gap-6 flex-1">
                        <div className="mt-1">
                          {getCelebrationIcon(component)}
                        </div>
                        
                        <div className="flex-1">
                          <div className="mb-3">
                            <span className="inline-block px-4 py-1 bg-[var(--muted)] text-[var(--primary)] text-xs uppercase tracking-widest rounded-full font-semibold">
                              {component.type || 'Event'}
                            </span>
                          </div>
                          
                          <h3 className="font-serif text-2xl text-[var(--foreground)] mb-4 tracking-wide">
                            {component.title || `Event ${index + 1}`}
                          </h3>

                          {/* Event Details */}
                          {component.data && Object.keys(component.data).length > 0 && (
                            <div className="grid md:grid-cols-2 gap-4">
                              {Object.entries(component.data).map(([key, value]) => {
                                if (!value || key === 'isOption' || key === 'optionDescription') return null;
                                
                                const isPriceField = key.toLowerCase().includes('price') || 
                                                     key.toLowerCase().includes('cost') || 
                                                     key.toLowerCase().includes('fee') ||
                                                     key.toLowerCase().includes('total') ||
                                                     key.toLowerCase().includes('deposit');
                                const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                                
                                let displayValue = String(value);
                                if (isPriceField && isNumeric) {
                                  displayValue = formatCurrency(parseFloat(value));
                                }
                                
                                return (
                                  <div key={key} className="flex items-start gap-2">
                                    <Sparkles className="w-4 h-4 text-[var(--accent)] mt-1 flex-shrink-0" />
                                    <div>
                                      <div className="text-xs text-[var(--muted-foreground)] uppercase tracking-wide">
                                        {humanizeKey(key)}
                                      </div>
                                      <div className="text-[var(--foreground)] font-medium">
                                        {displayValue}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Component Photos */}
                          {component.photos && component.photos.length > 0 && (
                            <div className="mt-6">
                              <h4 className="text-sm font-serif font-bold text-[var(--foreground)] mb-3 flex items-center gap-2">
                                <span>📸</span> Photos
                              </h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {component.photos.map((photo, photoIndex) => (
                                  <div 
                                    key={photoIndex} 
                                    className="relative group cursor-pointer rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border-2 border-[var(--border)] bg-white"
                                    onClick={() => window.open(photo.url, '_blank')}
                                  >
                                    <img 
                                      src={photo.url} 
                                      alt={photo.name || 'Component photo'} 
                                      className="w-full h-auto object-contain"
                                      style={{ maxHeight: '300px' }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--primary)]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                                      <span className="text-[var(--background)] text-sm font-semibold">{photo.name || 'View Photo'}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Component Documents */}
                          {component.documents && component.documents.length > 0 && (
                            <div className="mt-6">
                              <h4 className="text-sm font-serif font-bold text-[var(--foreground)] mb-3 flex items-center gap-2">
                                <span>📋</span> Documents
                              </h4>
                              <div className="space-y-2">
                                {component.documents.map((doc, docIndex) => (
                                  <a 
                                    key={docIndex}
                                    href={doc.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 bg-[var(--muted)] hover:brightness-95 px-4 py-2 rounded-2xl transition-colors border border-[var(--border)]"
                                  >
                                    <span className="text-lg">📄</span>
                                    <span className="text-[var(--foreground)] text-sm font-semibold">{doc.name}</span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {hasMultipleOptions(component) && (
                            <div className="mt-6" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }}>
                              <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>Choose Your Option</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {getComponentOptions(component).map((option, optIndex) => (
                                  <div key={option.id || optIndex} className="rounded-xl overflow-hidden" style={{ border: `2px solid ${optIndex === 0 ? 'var(--accent)' : 'var(--border)'}` }}>
                                    {option.photoUrl && (
                                      <img src={option.photoUrl} alt={option.label || 'Option'} className="w-full h-28 object-cover" />
                                    )}
                                    <div className="p-4">
                                      <div className="flex items-center justify-between gap-2 mb-1">
                                        <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{option.label || `Option ${optIndex + 1}`}</span>
                                        {option.badge && (
                                          <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--accent)' }}>{option.badge}</span>
                                        )}
                                      </div>
                                      {option.description && (
                                        <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>{option.description}</p>
                                      )}
                                      <p className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{formatCurrency(getOptionTotal(option))}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="text-xs text-[var(--muted-foreground)] uppercase tracking-widest mb-2">Investment</div>
                        <div className="font-serif text-2xl text-[var(--foreground)]">
                          {formatCurrency(component.price)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Part 3: Travel Logistics */}
        {logistics.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-semibold text-[var(--foreground)] mb-2">Travel Logistics</h3>
              <p className="text-[var(--muted-foreground)] text-sm">Getting you there and back</p>
            </div>

            <div className="space-y-4">
              {logistics.map((component, index) => (
                <div
                  key={component.id || index}
                  className="bg-white rounded-lg border border-[var(--border)] p-6 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    {component.type?.toLowerCase().includes('flight') ? (
                      <Plane className="w-5 h-5 text-[var(--muted-foreground)]" />
                    ) : (
                      <Car className="w-5 h-5 text-[var(--muted-foreground)]" />
                    )}
                    <div>
                      <div className="font-semibold text-[var(--foreground)]">{component.title}</div>
                      <div className="text-sm text-[var(--muted-foreground)]">{component.type}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-[var(--foreground)]">{formatCurrency(component.price)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Special Notes */}
        {quote.notes && (
          <div className="mb-20 bg-[var(--muted)] rounded-xl border-2 border-[var(--border)] p-10">
            <div className="flex items-center gap-3 mb-6">
              <Heart className="w-6 h-6 text-[var(--primary)]" />
              <h3 className="font-serif text-2xl text-[var(--foreground)]">Special Notes</h3>
            </div>
            <p className="text-[var(--foreground)] leading-relaxed whitespace-pre-wrap font-light">
              {quote.notes}
            </p>
          </div>
        )}

        {/* Pricing Summary - Elegant */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-2 border-[var(--border)]">
          <div className="p-12 text-center">
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-20 h-px bg-[var(--accent)]"></div>
              <Heart className="w-8 h-8 text-[var(--primary)]" />
              <div className="w-20 h-px bg-[var(--accent)]"></div>
            </div>

            <h3 className="font-serif text-3xl text-[var(--foreground)] mb-8 tracking-wide">Your Wedding Investment</h3>

            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-8 mb-8 border border-[var(--border)]">
              <div className="text-[var(--muted-foreground)] text-sm uppercase tracking-widest mb-3">Total Investment</div>
              <div className="font-serif text-6xl text-[var(--foreground)] mb-2">
                {formatCurrency(quote.totalPrice)}
              </div>
              <p className="text-[var(--muted-foreground)] font-light">For your dream destination wedding</p>
            </div>

            {quote.depositAmount > 0 && (
              <div className="grid md:grid-cols-2 gap-6 mb-10">
                <div className="bg-[var(--muted)] rounded-xl p-6 border border-[var(--border)]">
                  <div className="text-[var(--primary)] text-xs uppercase tracking-widest mb-2 font-semibold">Initial Deposit</div>
                  <div className="font-serif text-3xl text-[var(--foreground)]">{formatCurrency(quote.depositAmount)}</div>
                </div>
                {quote.depositDueDate && (
                  <div className="bg-[var(--muted)] rounded-xl p-6 border border-[var(--border)]">
                    <div className="text-[var(--primary)] text-xs uppercase tracking-widest mb-2 font-semibold">Due Date</div>
                    <div className="text-2xl text-[var(--foreground)] font-light">{formatDate(quote.depositDueDate)}</div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Button - Rose Gold Style */}
            {onPayment && (
              <div>
                <button
                  onClick={onPayment}
                  disabled={isAgentPreview}
                  className={`w-full bg-[var(--primary)] hover:brightness-110 text-[var(--background)] font-serif py-6 px-8 rounded-xl text-xl tracking-wide transition-all duration-300 flex items-center justify-center gap-3 ${
                    isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'shadow-xl hover:shadow-2xl hover:scale-105'
                  }`}
                >
                  <CheckCircle2 className="w-6 h-6" />
                  {isAgentPreview ? 'Preview Mode' : 'Approve Proposal'}
                  <Heart className="w-6 h-6" />
                </button>
                {!isAgentPreview && (
                  <p className="text-[var(--muted-foreground)] text-sm mt-6 font-light">
                    Begin your journey to forever
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 bg-[var(--muted)] rounded-3xl border-2 border-[var(--border)] overflow-hidden shadow-xl">
            <div className="bg-[var(--primary)] px-6 py-5">
              <div className="flex items-center justify-center gap-3 text-[var(--background)]">
                <Heart className="w-6 h-6" />
                <h3 className="text-lg font-serif font-bold">Your Wedding Specialist</h3>
                <Heart className="w-6 h-6" />
              </div>
            </div>
            
            <div className="p-6">
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-xl font-serif font-bold text-[var(--foreground)]">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-[var(--primary)] font-semibold">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-3 max-w-md mx-auto">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-3 bg-white px-4 py-3 rounded-2xl hover:bg-[var(--muted)] transition-colors border border-[var(--border)] shadow-sm"
                  >
                    <Mail className="w-5 h-5 text-[var(--primary)]" />
                    <span className="text-[var(--foreground)] font-medium">{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-3 bg-white px-4 py-3 rounded-2xl hover:bg-[var(--muted)] transition-colors border border-[var(--border)] shadow-sm"
                  >
                    <Phone className="w-5 h-5 text-[var(--primary)]" />
                    <span className="text-[var(--foreground)] font-medium">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 text-center">
          <div className="flex items-center justify-center gap-4 text-[var(--muted-foreground)]">
            <div className="w-16 h-px bg-[var(--accent)]"></div>
            <Heart className="w-5 h-5" />
            <span className="font-serif text-sm tracking-widest">With Love</span>
            <Heart className="w-5 h-5" />
            <div className="w-16 h-px bg-[var(--accent)]"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DestinationWeddingTemplate;
